import type { HelpArticle } from "../../types";

export const loadCalcsArticle: HelpArticle = {
  id: "load-calcs",
  title: "Manual load calculations",
  description:
    "Choose a manual load family, enter its source data, and understand exactly when Firmark saves and runs the authoritative load engine.",
  tags: ["load calculation", "wind", "snow", "seismic", "gravity", "manual"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "Manual load calculations turn hazard, geometry, occupancy, and assembly inputs into reviewable load records. They generate loads; they do not design a member, report a pass ratio, or publish support reactions. Pick the calculator that matches the load you are establishing, complete its input groups, and let the server run the same load engine used by the calculation API.",
    },

    { kind: "heading", level: 2, text: "Choose the load family", id: "choose-family" },
    { kind: "where", path: ["Calculations", "Project", "Add calculation", "Load calculations"] },
    {
      kind: "paragraph",
      text: "The catalog contains ten manual load calculators across six catalog families. The calculator determines the input groups, diagram, case count, result table, and source contract shown in the workspace:",
    },
    {
      kind: "table",
      caption: "The manual load inventory in the Add calculation catalog.",
      headers: ["Family", "What its record establishes"],
      rows: [
        ["Wind — MWFRS", "Directional and low-rise envelope pressures for the main wind-force-resisting system."],
        ["Wind — Components & Cladding", "Zone pressures for walls, roofs, fasteners, sheathing, and overhang components."],
        ["Snow — Balanced, Unbalanced & Drift", "Balanced and unbalanced roof cases, roof-step drift, and obstruction drift."],
        ["Seismic — Equivalent Lateral Force", "Base shear, vertical force distribution, story shear, and axis-specific SFRS cases."],
        ["Dead & Gravity Loads", "Assembly dead loads, manual plan regions, weights, and tributary summaries."],
        ["Roof Live Load", "Roof live-load basis with tributary-area and slope-reduction review."],
        ["Floor & Deck Live Loads", "Floor, deck, balcony, and occupancy live-load regions."],
        ["Ceiling & Attic Live Loads", "Ceiling live load and attic-storage extents from clear headroom."],
        ["Rain & Atmospheric Ice Loads", "Drainage head, ponding applicability, and atmospheric-ice conditions."],
        ["Wall Gravity Loads", "Wall-face dead load converted to top, base, and opening-adjusted line loads."],
      ],
    },
    {
      kind: "shot",
      shotId: "load-catalog-families",
      caption: "All ten load generators live together below the member calculators. The card names the standard and the record it creates.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "A family name is not an engineering approval",
      body: "The catalog identifies implemented calculators; it does not decide whether a procedure, reduction, hazard value, drainage assumption, or load family is appropriate for the project. Establish that design basis before using the record.",
    },

    { kind: "heading", level: 2, text: "Read the workspace before entering data", id: "workspace" },
    { kind: "where", path: ["Calculations", "Project", "Load sheet", "Inputs"] },
    {
      kind: "paragraph",
      text: "A persisted manual sheet has the same working anatomy across all ten calculators. The header keeps the mark, calculator, standard, and current state visible. The left pane holds grouped inputs and the source contract. The right pane holds the summary, an explanatory diagram, the engine trace, result tables, and disclosure lists. Changing calculators changes the fields, not this reading order.",
    },
    {
      kind: "shot",
      shotId: "load-manual-workspace",
      caption: "DG-1 in manual mode: sheet identity and status above, editable inputs at left, and the engine-authored record at right.",
    },
    {
      kind: "steps",
      steps: [
        {
          title: "Confirm the family and standard",
          body: "Read the header before entering values. A similar-looking field can have a different role in another calculator; the selected load template controls the server calculator id.",
        },
        {
          title: "Complete the open input groups",
          body: "Site and hazard, geometry, classification, occupancy, assembly, and policy groups appear only where their template defines them. Keep units with the field labels; the browser sends numeric values in those displayed units.",
        },
        {
          title: "Build every repeated row deliberately",
          body: "Table inputs such as roof planes, adjacencies, obstructions, stories, regions, and openings have Add and remove controls. Their text identifiers connect rows inside the same request, so a roof-step adjacency must name roof planes that exist in that sheet.",
        },
        {
          title: "Review the source contract",
          body: "The contract names the engine handoff used for this family. It identifies the implementation boundary; it is not a substitute for checking the assumptions, deferred items, and sources returned by the run.",
        },
      ],
    },
    {
      kind: "shot",
      shotId: "load-repeatable-inputs",
      caption: "Snow keeps roof planes, roof-step adjacencies, and obstructions as separate repeatable ledgers. Their identifiers make the geometry relationship explicit.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Dead-gravity tributary modes are not equivalent",
      body: "Use **Manual region** for a computed manual dead-gravity region. The visible **Automation geometry** and **Linked calculation** choices currently remain review-only binding labels; they do not import geometry or another calculation into this manual engine run.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "C&C roof type is not a pressure input",
      body: "In Wind — Components & Cladding, **Roof type** does not change pressure math. It survives only as resolved-input traceability metadata, and the visible trace omits the returned **Roof classification** field. Roof pitch drives coefficient lookup. This mismatch blocks publication.",
    },

    { kind: "heading", level: 2, text: "Commit and calculate", id: "commit-and-calculate" },
    {
      kind: "paragraph",
      text: "On an existing sheet, changing a field marks the header Pending. Leaving that field starts a quiet authoritative run: the browser saves the input packet underneath the run while the visible header moves to Calculating. The explicit **Recalculate** button saves the current inputs and repeats the server run even when no field is dirty. Use it when you need a deliberate confirmation after reviewing the complete packet.",
    },
    {
      kind: "paragraph",
      text: "A rejected explicit run shows the engine's refusal in the workspace. A rejected quiet field-exit run is currently suppressed in the interface, so do not interpret the absence of a pop-up as acceptance: check the status and use **Recalculate** to surface a refusal. Saving changed inputs invalidates the sheet's prior stored result before a new successful calculation replaces it.",
    },
    {
      kind: "definition-list",
      items: [
        { term: "Draft", definition: "The sheet exists, but no current engine result is stored." },
        { term: "Pending", definition: "The browser has changed inputs that have not completed the save-and-run cycle." },
        { term: "Calculated", definition: "The engine returned a complete load result for the stored inputs." },
        { term: "Partial", definition: "The engine computed part of the record and named work that remains deferred or requires review." },
        { term: "Blocked", definition: "The engine refused to produce the requested load record from the supplied packet." },
      ],
    },
    {
      kind: "callout",
      variant: "warning",
      title: "The workspace factor sentence is inaccurate for snow",
      body: "The current source-contract sentence says **Results remain nominal and unfactored** for every manual sheet. That is accurate for nine load calculators, but not for snow: snow returns a reliability-targeted basis with LRFD 1.0 and ASD 0.7. Read the result basis and treat the blanket workspace sentence as a publication blocker.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Current source identifiers are not uniformly portable",
      body: "Some load results expose machine-local absolute policy or dataset paths; others return internal implementation or catalog identifiers. Those values can trace the running code, but they are not canonical, portable engineering citations. Until the result contract separates portable source citations from implementation trace, this blocks publication.",
    },
    {
      kind: "paragraph",
      text: "Diagrams are explanatory, not calculation records. Wall gravity currently draws an opening when stored opening area is zero; rely on the returned summary, trace, tables, and disclosures. Manual load generators lack member reaction packets. They can remain visible in **Link reaction** as unavailable sheets with no reactions, but they cannot be selected as reaction sources.",
    },

    { kind: "related", topics: ["load-calc-results", "wall-assembly", "linking-reactions"] },
  ],
};
